import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import DefaultAuth from 'layouts/auth/Default';
import illustration from 'assets/img/auth/auth.jpg';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { useAuth } from '../../../contexts/AuthContext';
import authService from '../../../services/authService';

function SignIn() {
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const brandStars = useColorModeValue('brand.500', 'brand.400');

  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [userError, setUserError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();
  const toast = useToast();

  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      toast({
        title: 'Sesión expirada',
        description:
          'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      sessionStorage.removeItem('sessionExpired');
    }
  }, [toast]);

  const handleClick = () => setShow(!show);

  const handleLogin = async () => {
    let hasError = false;

    if (!user) {
      setUserError(true);
      hasError = true;
    } else {
      setUserError(false);
    }

    if (!password) {
      setPasswordError(true);
      hasError = true;
    } else {
      setPasswordError(false);
    }

    if (hasError) {
      toast({
        title: 'Campos obligatorios',
        description: 'Por favor, completa los campos requeridos.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      console.log('=== INICIANDO LOGIN ===');
      console.log('User:', user);
      
      // Usar authService para hacer login completo
      const response = await authService.login(user, password);
      
      console.log('=== LOGIN EXITOSO ===');
      console.log('Response:', response);
      
      if (response.success || response.payload) {
        login(); // Notificar al contexto
        
        // Verificar que los datos se guardaron
        const userData = localStorage.getItem('userData');
        console.log('userData guardado en localStorage:', userData);
        
        const signedUser = response.payload?.user;
        toast({
          title: signedUser?.mustChangePassword
            ? 'Debes cambiar la contraseña'
            : 'Bienvenido',
          description: signedUser?.mustChangePassword
            ? 'La contraseña actual es temporal.'
            : 'Has iniciado sesión correctamente.',
          status: signedUser?.mustChangePassword ? 'warning' : 'success',
          duration: 3000,
          isClosable: true,
        });
        
        navigate('/admin');
      }
    } catch (error) {
      console.error('Error en login:', error);
      toast({
        title: 'Usuario o contraseña incorrectos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Bienvenido
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Ingresar usuario y contraseña para ingresar
          </Text>
        </Box>
        <form
          onSubmit={(e) => {
            e.preventDefault(); // evita que recargue la página
            handleLogin();
          }}
        >
          <Flex
            zIndex="2"
            direction="column"
            w={{ base: '100%', md: '420px' }}
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: 'auto', lg: 'unset' }}
            me="auto"
            mb={{ base: '20px', md: 'auto' }}
          >
            <FormControl isInvalid={userError} mb="4">
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Usuario<Text color={brandStars}>*</Text>
              </FormLabel>
              <Input
                variant="auth"
                fontSize="sm"
                type="text"
                placeholder="usuario"
                fontWeight="500"
                size="lg"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                isRequired
              />
            </FormControl>

            <FormControl isInvalid={passwordError} mb="6">
              <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Password<Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size="md">
                <Input
                  fontSize="sm"
                  placeholder="Min. 8 characters"
                  size="lg"
                  type={show ? 'text' : 'password'}
                  variant="auth"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isRequired
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Button
              type="submit"
              fontSize="sm"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
              bg="#003064"
              color="white"
              _hover={{ bg: '#1A365D' }}
              _active={{ bg: '#003064' }}
              _focus={{
                bg: '#003064',
                boxShadow: '0 0 0 2px #718096',
                outline: 'none',
              }}
            >
              Iniciar sesión
            </Button>
          </Flex>
        </form>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
